import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { colors } from "../../../ui/colors";
import { Email } from "@mui/icons-material";
import { useVerificationCode } from "../hooks/useVerificationCode";
import { useResendCooldown } from "../hooks/useResendCooldown";
import { VerificationCodeInput } from "../components/VerifyCodeInput";
import { useTwoFa } from "../hooks/useTwoFa";
import { SnackbarAlert } from "../../../common/SnackbarAlert";



export const EmailVerificationForm = ({
  email,
  onBack,
  onSuccess,
}) => {
  const { isLoading, verifyTwoFa } = useTwoFa();
  const [showContent, setShowContent] = useState(false);
   const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("");
  const {
    code,
    inputsRef,
    handleChange,
    handleKeyDown,
    handlePaste,
    getCodeValue,
    isCodeComplete,
  } = useVerificationCode(5);

  const {
    cooldown,
    isResending,
    setIsResending,
    startCooldown,
    canResend,
  } = useResendCooldown(60);


  //Animacion de entrada
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);


  //Envio de twoFA
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isCodeComplete) return;
    try {
      await verifyTwoFa("email", getCodeValue());
      onSuccess();
    } catch (error) {
      console.error("Error verifying email:", error);
    setAlertMessage("Error verifying email"); 
    setAlertSeverity("error");
    setOpenAlert(true); 
    }
  };

  // const handleResendEmail = async () => {
  //   if (!canResend) return;

  //   setIsResending(true);
  //   try {
  //     await gateway.post('/verification/resend-email', { email });
  //     showSnackbar('Verification code sent!', 'success');
  //     startCooldown();
  //   } catch (error) {
  //     const errorMessage = error?.response?.data?.message || 'Failed to send code';
  //     showSnackbar(errorMessage, 'error');
  //   } finally {
  //     setIsResending(false);
  //   }
  // };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        borderRadius: "16px",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        p: { xs: 4, md: 6 },
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(251, 142, 0, 0.1)",
        transform: showContent ? "translateY(0)" : "translateY(20px)",
        opacity: showContent ? 1 : 0,
        transition: "all 400ms ease-out",
      }}
    >
      {/* Header icon */}
      <Box sx={{ display: "inline-flex", mb: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${colors.orange.base} 0%, ${colors.yellow.base} 100%)`,
            boxShadow: "0 8px 24px rgba(251, 142, 0, 0.25)",
          }}
        >
          <Email sx={{ fontSize: 40, color: "white" }} />
        </Box>
      </Box>

      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          fontSize: { xs: "24px", md: "28px" },
          mb: 2,
          color: colors.gray[800],
        }}
      >
        Check Your Email
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
        <Typography
          variant="body1"
          sx={{
            color: colors.gray[400],
            textAlign: 'center',
            mb: 3,
            lineHeight: 1.5
          }}
        >
          We've sent a verification code to{' '}
          <Box component="span" sx={{ fontWeight: 600, color: colors.gray[600] }}>
            {email}
          </Box>
          . Please enter the 5-digit code below.
        </Typography>

        <VerificationCodeInput
          code={code}
          inputsRef={inputsRef}
          handleChange={handleChange}
          handleKeyDown={handleKeyDown}
          handlePaste={handlePaste}
          isDisabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading || !isCodeComplete}
          sx={{
            py: 1.5,
            mb: 2,
            borderRadius: '12px',
            backgroundColor: colors.orange.base,
            color: 'white',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: colors.orange[600],
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: colors.gray[200],
              color: colors.gray[400],
            },
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
        </Button>

        {/* Resend button */}
        <Button
          fullWidth
          variant="text"
          // onClick={handleResendEmail}
          disabled={!canResend}
          sx={{
            py: 1,
            mb: 2,
            color: !canResend ? colors.gray[400] : colors.orange.base,
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: colors.orange[50],
            },
            '&:disabled': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {isResending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Sending...
            </Box>
          ) : cooldown > 0 ? (
            `Resend in ${cooldown}s`
          ) : (
            "Resend Email"
          )}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={onBack}
          sx={{
            py: 1,
            color: colors.orange.base,
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: colors.orange[50],
            },
          }}
        >
          Use a Different Method
        </Button>
<SnackbarAlert
  openState={openAlert}
  setOpenState={setOpenAlert}
  severity={alertSeverity}
  duration={4500}
  message={alertMessage}
/>

      </Box>
    </Box>
  );
};